import { useEffect } from 'react';
import { alpha, Box, Container, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getClerkPublishableKey } from './clerkPublishableKey';
import { useHomeHero } from './HomeHeroContext';
import SiteHeaderAccount from './SiteHeaderAccount';

const LOGO = 32;
const LOGO_RADIUS = 8;

export default function SiteHeader() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { heroVisible, setHeroVisible } = useHomeHero();

  useEffect(() => {
    if (pathname === '/') {
      setHeroVisible(true);
    }
  }, [pathname, setHeroVisible]);

  if (pathname === '/account' && getClerkPublishableKey()) {
    return <SiteHeaderAccount />;
  }

  const isHome = pathname === '/';
  /** Home: fixed bar so mounting does not reflow the page (Safari jump when switching from `return null`). */
  const homeHeaderHidden = isHome && heroVisible;

  let title = null;
  if (pathname === '/account') title = t('account.title');
  if (pathname === '/web-privacy') title = t('nav.webPrivacy');
  if (pathname === '/web-offer') title = t('nav.webOffer');

  const showHomeBrand = isHome && !heroVisible;
  const hasLeft = Boolean(title) || showHomeBrand;

  return (
    <Box
      component="header"
      aria-hidden={homeHeaderHidden ? true : undefined}
      sx={{
        ...(isHome
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              transform: homeHeaderHidden ? 'translate3d(0, -100%, 0)' : 'translate3d(0, 0, 0)',
              transition: 'transform 0.2s ease-out',
              pointerEvents: homeHeaderHidden ? 'none' : 'auto',
              backfaceVisibility: 'hidden',
            }
          : {
              position: 'sticky',
              top: 0,
            }),
        zIndex: 1100,
        py: 1,
        color: 'text.primary',
        backgroundColor: alpha('#090b0d', 0.52),
        backdropFilter: 'blur(18px) saturate(140%)',
        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
        boxShadow: `0 12px 40px ${alpha('#000', 0.16)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.015))',
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={hasLeft ? 'space-between' : 'flex-end'}
          gap={2}
          height={40}
        >
          {title ? (
            <Box display="flex" alignItems="center" gap={2} minWidth={0} flex="1 1 auto">
              <Box
                component={RouterLink}
                to="/"
                aria-label={t('nav.back')}
                sx={{
                  flexShrink: 0,
                  display: 'block',
                  width: LOGO,
                  height: LOGO,
                  borderRadius: `${LOGO_RADIUS}px`,
                  overflow: 'hidden',
                  bgcolor: alpha('#000000', 0.8),
                  lineHeight: 0,
                  textDecoration: 'none',
                  color: 'inherit',
                  boxShadow: `0 8px 24px ${alpha('#000', 0.22)}`,
                }}
              >
                <img src="/lett-logo.webp" alt="" width={LOGO} height={LOGO} />
              </Box>
              <Typography variant="h6" noWrap sx={{ fontWeight: 700, minWidth: 0 }}>
                {title}
              </Typography>
            </Box>
          ) : showHomeBrand ? (
            <Box display="flex" alignItems="center" gap={2} minWidth={0} flex="1 1 auto">
              <Box
                component={RouterLink}
                to="/"
                aria-label={t('nav.back')}
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{
                  flexShrink: 0,
                  display: 'block',
                  width: LOGO,
                  height: LOGO,
                  borderRadius: `${LOGO_RADIUS}px`,
                  overflow: 'hidden',
                  bgcolor: alpha('#000000', 0.8),
                  lineHeight: 0,
                  textDecoration: 'none',
                  color: 'inherit',
                  boxShadow: `0 8px 24px ${alpha('#000', 0.22)}`,
                }}
              >
                <img src="/lett-logo.webp" alt={t('app.lettAlt')} width={LOGO} height={LOGO} />
              </Box>
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                {t('app.name')}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
}
